import { useAppContext } from '@app/react-common/AppContext';
import ThreeDotMenu from '@app/react-common/components/three-dot-menu/ThreeDotMenu';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AbTestModals from '../../AbTestModals/AbTestModals';
import { useAbTestModals } from '../../AbTestModals/hooks/useAbTestModal';
import {
  AbTestCardProps,
  SegmentMetric,
  SegmentFieldMapping,
  DEFAULT_FIELDS,
  NO_OUTLIERS_FIELDS,
} from './AbTestCard.types';
import {
  getAbTestStatusText,
  getAbTestThreeDotMenuItems,
  mapSegmentName,
  mapCrossSiteSegmentName,
  SKIP_AB_TEST_PREDICTED_SEGMENTS,
  PREDICTED_SEGMENTS_MAPPING,
  createAbTestSegmentPairs,
  createAbTestWinnerVariantDto,
  formatAbTestDateRange,
} from './utils';
import AbTestSegmentsMetricsTable from './components/AbTestSegmentsMetricsTable/AbTestSegmentsMetricsTable';
import StatusLabel from './components/StatusLabel/StatusLabel';
import TestVariantsTable, { transformAbTestVariantToTestVariant } from './components/TestVariantTable/TestVariantTable';
import { formatters } from './components/GenericMetricsTable/utils';
import { AbTestType } from '../../../models/AbTestType';
import { RuleChips } from '@app/react-common/components/rule-chip/RuleChip';
import { useAbTestingApi } from '../../../api/useAbTestingApi';
import Button from '@app/react-common/components/button/Button';
import { AbTestWinnerOptions } from '../../../models/AbtestWinnerOptions';
import { EndAbTestModal } from '../../end-ab-test-modal/EndAbTestModal';
import { ConfirmationModal } from '@app/react-common/components/confirmation-modal/ConfirmationModal';
import { AbTest } from '../../../models/AbTest';
import { SegmentMetrics } from './components/AbTestSegmentsMetricsTable/AbTestSegmentsMetricsTable.types';
import { ReviewAbTestModal } from '../../review-ab-test-modal/ReviewAbTestModal';
import { AbTestTypeCategory, AbTestWidgetType, AbTestWidgetTypeLabels } from '../../../models/AbTestCreationType';
import NoDataFallback from './components/NoDataFallback/NoDataFallback';
import Tag from '@app/react-common/components/tag/Tag';
import { getIsThemeTest, getIsWinnerAppliedInShopify, getWinnerButtonText } from '../../../utils';
import { ABTestOutlierFilterOption } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutVariantABTest';

function processSegmentMetrics(
  metrics: SegmentMetric[],
  isThemeTest: boolean,
  fields: SegmentFieldMapping,
): SegmentMetrics[] {
  return metrics
    .filter((segment) => {
      if (isThemeTest) return true;
      return (
        segment.SEGMENT in PREDICTED_SEGMENTS_MAPPING && !SKIP_AB_TEST_PREDICTED_SEGMENTS.includes(segment.SEGMENT)
      );
    })
    .map((segment) => ({
      segmentName: isThemeTest ? mapCrossSiteSegmentName(segment.SEGMENT) : mapSegmentName(segment.SEGMENT),
      segmentId: segment.SEGMENT,
      statSig: segment[isThemeTest ? fields.statSigUcvr : fields.statSigArpc] as number,
      checkouts: segment[isThemeTest ? fields.totalUsers : fields.totalCheckouts] as number,
      traffic: segment[fields.percentTraffic] as number,
      conversionRate: segment[isThemeTest ? fields.upliftUcvr : fields.upliftCr] as number,
      arpc: segment[isThemeTest ? fields.upliftArpu : fields.upliftArpc] as number,
      aov: segment[fields.upliftAov] as number,
      annualizedLift: segment[fields.annualizedLift] as number,
    }))
    .sort((a, b) => b.statSig - a.statSig);
}

export default function AbTestCard({
  abTest,
  setActiveTab,
  checkouts,
  extensions,
  extensionGroups,
  runningAbTestCheckoutIds,
  abTestActions: parentAbTestActions,
  freeShippingThreshold,
  bigSpenderThreshold,
  themes,
  productPages,
  smartCarts,
  checkoutProfiles,
}: AbTestCardProps) {
  const { angularRedirect, showToast } = useAppContext();
  const {
    modalStates,
    modalActions,
    setAsWinnerOpen,
    setSetAsWinnerOpen,
    forceSetAsWinnerOpen,
    setForceSetAsWinnerOpen,
  } = useAbTestModals();

  // Use parent's abTestActions for state updates (list management)
  // Metrics are component-specific (each card has different test metrics), so manage locally
  const abTestActions = parentAbTestActions;
  const [segmentMetrics, setSegmentMetrics] = useState<SegmentMetric[]>([]);
  const [testVariantMetrics, setTestVariantMetrics] = useState<any[]>([]);
  const [totalAnnualizedLift, setTotalAnnualizedLift] = useState<Array<{
    segment: string;
    annualizedUpliftArpc: number;
  }> | null>(null);

  // Use API directly for metrics (no interceptor needed, component handles errors)
  const abTestingApi = useAbTestingApi(false);
  const [isSegmentMetricLoading, setIsSegmentMetricLoading] = useState(false);
  const [isTestVariantsLoading, setIsTestVariantsLoading] = useState(false);
  const [endAbTestOpen, setEndAbTestOpen] = useState(false);
  const [reviewAbTestModalOpen, setReviewAbTestModalOpen] = useState(false);

  const [reviewModalData, setReviewModalData] = useState<{
    comments: string;
    selectedActionOption: string | null;
    selectedSegments: SegmentMetrics[] | null; // Store only source data, construct pairs on-demand
    outlierFilter: ABTestOutlierFilterOption;
  } | null>(null);

  const isDraftMode = getAbTestStatusText(abTest) === AbTestType.DRAFT;
  const isConcludedMode = getAbTestStatusText(abTest) === AbTestType.CONCLUDED;
  const isCorrectTestTypeForSetWinner =
    abTest.testType === AbTestTypeCategory.SHIPPING_RATES || getIsWinnerAppliedInShopify(abTest.testType);
  const showSetWinnerButton =
    isConcludedMode &&
    abTest.winnerAt === null &&
    testVariantMetrics.length > 0 &&
    abTest.abTestVariants?.length === 2 &&
    isCorrectTestTypeForSetWinner;

  // Helper: Get winner variant ID (B variant, or first variant if only one)
  const getWinnerVariantId = useCallback(
    (selectedActionOption: string | null): number | null => {
      if (selectedActionOption === AbTestWinnerOptions.dontImplementAnyChange) {
        const variantA = abTest.abTestVariants.find((v) => v.name === 'A') || abTest.abTestVariants[0];
        return variantA?.id ?? null;
      }
      if (abTest.abTestVariants?.length >= 2) {
        // Find variant B (test variant), fallback to index 1 if not found by name
        const variantB = abTest.abTestVariants.find((v) => v.name === 'B') || abTest.abTestVariants[1];
        return variantB?.id ?? null;
      }
      return abTest.abTestVariants?.[0]?.id ?? null;
    },
    [abTest.abTestVariants],
  );

  const testVariants = useMemo(() => {
    if (!abTest.abTestVariants?.length || isTestVariantsLoading || (!isDraftMode && testVariantMetrics.length === 0)) {
      return [];
    }

    const isTwoVariants = abTest.abTestVariants.length === 2;
    const isThemeTest = getIsThemeTest(abTest.testType);
    const isWithoutOutlier = abTest.outlierFilter === ABTestOutlierFilterOption.WITHOUT_OUTLIER;

    return abTest.abTestVariants
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((variant) => {
        // Use real metrics from hook state if available, otherwise fallback to mock data
        let metrics = null;

        if (!isDraftMode) {
          if (isTwoVariants) {
            // For 2 variants:
            // Cross-site tests: CI_AB_ORIGINAL_OR_TEST uses 'A', 'B' directly (no segment filtering)
            // Checkout tests: CI_AB_ORIGINAL_OR_TEST uses 'original'/'test', filter by 'all_checkouts'
            const originalTestVariantMetrics = isThemeTest
              ? testVariantMetrics.find(
                  (metric) => metric.SEGMENT === 'all_users' && metric.CI_AB_ORIGINAL_OR_TEST === 'A',
                )
              : testVariantMetrics.find(
                  (metric) => metric.SEGMENT === 'all_checkouts' && metric.CI_AB_ORIGINAL_OR_TEST === 'original',
                );
            const testTestVariantMetrics = isThemeTest
              ? testVariantMetrics.find(
                  (metric) => metric.SEGMENT === 'all_users' && metric.CI_AB_ORIGINAL_OR_TEST === 'B',
                )
              : testVariantMetrics.find(
                  (metric) => metric.SEGMENT === 'all_checkouts' && metric.CI_AB_ORIGINAL_OR_TEST === 'test',
                );

            if (isThemeTest) {
              const variantMetrics = variant.name === 'A' ? originalTestVariantMetrics : testTestVariantMetrics;
              metrics = {
                users: isWithoutOutlier ? variantMetrics?.USERS_NO_OUTLIERS : variantMetrics?.USERS,
                cvr: isWithoutOutlier ? variantMetrics?.UCVR_NO_OUTLIERS : variantMetrics?.UCVR,
                statSig: isWithoutOutlier
                  ? variantMetrics?.STATISTICAL_SIGNIFICANCE_UCVR_NO_OUTLIERS
                  : variantMetrics?.STATISTICAL_SIGNIFICANCE_UCVR,
                aov: isWithoutOutlier ? variantMetrics?.AOV_NO_OUTLIERS : variantMetrics?.AOV,
                arpu: isWithoutOutlier ? variantMetrics?.ARPU_NO_OUTLIERS : variantMetrics?.ARPU,
              };
            } else {
              const variantMetrics = variant.name === 'A' ? originalTestVariantMetrics : testTestVariantMetrics;
              metrics = {
                convRate: isWithoutOutlier ? variantMetrics?.CR_NO_OUTLIERS : variantMetrics?.CR,
                arpc: isWithoutOutlier ? variantMetrics?.ARPC_NO_OUTLIERS : variantMetrics?.ARPC,
                aov: isWithoutOutlier ? variantMetrics?.AOV_NO_OUTLIERS : variantMetrics?.AOV,
                checkouts: isWithoutOutlier ? variantMetrics?.CHECKOUTS_NO_OUTLIERS : variantMetrics?.CHECKOUTS,
              };
            }
          } else {
            // For more than 2 variants, CI_AB_ORIGINAL_OR_TEST will be 'A', 'B', 'C', etc.
            const variantMetrics = isThemeTest
              ? testVariantMetrics.find(
                  (metric) => metric.SEGMENT === 'all_users' && metric.CI_AB_ORIGINAL_OR_TEST === variant.name,
                )
              : testVariantMetrics.find(
                  (metric) => metric.SEGMENT === 'all_checkouts' && metric.CI_AB_ORIGINAL_OR_TEST === variant.name,
                );

            if (isThemeTest) {
              metrics = variantMetrics
                ? {
                    users: isWithoutOutlier ? variantMetrics.USERS_NO_OUTLIERS : variantMetrics.USERS,
                    cvr: isWithoutOutlier ? variantMetrics.UCVR_NO_OUTLIERS : variantMetrics.UCVR,
                    statSig: isWithoutOutlier
                      ? variantMetrics.STATISTICAL_SIGNIFICANCE_UCVR_NO_OUTLIERS
                      : variantMetrics.STATISTICAL_SIGNIFICANCE_UCVR,
                    aov: isWithoutOutlier ? variantMetrics.AOV_NO_OUTLIERS : variantMetrics.AOV,
                    arpu: isWithoutOutlier ? variantMetrics.ARPU_NO_OUTLIERS : variantMetrics.ARPU,
                  }
                : null;
            } else {
              metrics = variantMetrics
                ? {
                    convRate: isWithoutOutlier ? variantMetrics.CR_NO_OUTLIERS : variantMetrics.CR,
                    arpc: isWithoutOutlier ? variantMetrics.ARPC_NO_OUTLIERS : variantMetrics.ARPC,
                    aov: isWithoutOutlier ? variantMetrics.AOV_NO_OUTLIERS : variantMetrics.AOV,
                    checkouts: isWithoutOutlier ? variantMetrics.CHECKOUTS_NO_OUTLIERS : variantMetrics.CHECKOUTS,
                  }
                : null;
            }
          }
        }

        return transformAbTestVariantToTestVariant(
          variant,
          abTest.testType,
          checkouts,
          extensions,
          extensionGroups,
          metrics,
          abTest.mode,
          themes,
          productPages,
          smartCarts,
          checkoutProfiles,
        );
      });
  }, [
    abTest.abTestVariants,
    checkouts,
    testVariantMetrics,
    isDraftMode,
    abTest.mode,
    abTest.outlierFilter,
    themes,
    productPages,
    smartCarts,
    checkoutProfiles,
    extensions,
    extensionGroups,
  ]);

  const segmentsMetricData: SegmentMetrics[] = useMemo(
    () => processSegmentMetrics(segmentMetrics, getIsThemeTest(abTest.testType), DEFAULT_FIELDS),
    [segmentMetrics, abTest.testType],
  );

  const segmentsMetricDataNoOutliers: SegmentMetrics[] = useMemo(
    () => processSegmentMetrics(segmentMetrics, getIsThemeTest(abTest.testType), NO_OUTLIERS_FIELDS),
    [segmentMetrics, abTest.testType],
  );

  // null keeps the analytics deep link on QuickSight's default filter
  const winningSegmentClean: string | null = useMemo(() => {
    if (!abTest.winningSegmentId) {
      return null;
    }
    const winningRow = segmentMetrics.find(
      (row) => row.SEGMENT?.toLowerCase() === abTest.winningSegmentId?.toLowerCase(),
    );
    return winningRow?.SEGMENT_MAPPING_CLEAN ?? null;
  }, [segmentMetrics, abTest.winningSegmentId]);

  const totalAnnualizedLiftDisplay = useMemo(() => {
    if (!isConcludedMode || !totalAnnualizedLift || totalAnnualizedLift.length === 0) {
      return null;
    }

    const roundedValue = Math.round(totalAnnualizedLift[0].annualizedUpliftArpc);
    if (roundedValue < 0) {
      return {
        formatted: 'No Positive Lift Detected',
        isNegative: true,
      };
    }
    return {
      formatted: `$${formatters.number(roundedValue)}`,
      isNegative: false,
    };
  }, [isConcludedMode, totalAnnualizedLift]);

  useEffect(() => {
    if (!isDraftMode && abTest.id) {
      const testId = abTest.id;
      const testTypeCategory = abTest.testType || AbTestTypeCategory.SHIPPING_RATES;
      const isThemeTest = getIsThemeTest(testTypeCategory);

      setIsTestVariantsLoading(true);
      abTestingApi
        .get(`/ab-tests/segments-metrics/${testId}?testTypeCategory=${testTypeCategory}&withVariant=true`)
        .then((response) => {
          setTestVariantMetrics(response.data);
          setIsTestVariantsLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching TestVariantMetrics:', error);
          setIsTestVariantsLoading(false);
        });

      setIsSegmentMetricLoading(true);
      abTestingApi
        .get(`/ab-tests/segments-metrics/${testId}?testTypeCategory=${testTypeCategory}&withVariant=false`)
        .then((response) => {
          setSegmentMetrics(response.data);
          setIsSegmentMetricLoading(false);

          if (isConcludedMode) {
            const segment = isThemeTest ? 'all_users' : 'all_checkouts';
            const outlierParam = abTest.outlierFilter ? `&outlierFilter=${abTest.outlierFilter}` : '';
            return abTestingApi.get(
              `/ab-tests/total-annualized-lift/${testId}?segments=${segment}&testTypeCategory=${testTypeCategory}${outlierParam}`,
            );
          }
        })
        .then((response) => {
          if (response && response.data) {
            const totalAnnualizedLiftResult = response.data;
            if (totalAnnualizedLiftResult === null || !Array.isArray(totalAnnualizedLiftResult)) {
              setTotalAnnualizedLift(null);
            } else {
              const mappedResult = totalAnnualizedLiftResult.map((row) => ({
                segment: row.Segment,
                annualizedUpliftArpc: row.annualizedUpliftArpc,
              }));
              setTotalAnnualizedLift(mappedResult.length > 0 ? mappedResult : null);
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching metrics:', error);
          setIsSegmentMetricLoading(false);
          setTotalAnnualizedLift(null);
        });
    }
  }, [isDraftMode, abTest.id, isConcludedMode, abTestingApi]);

  function handleSetWinner(abTest: AbTest) {
    const isThemeTest = getIsWinnerAppliedInShopify(abTest.testType);

    if (!isThemeTest) {
      const anyDeletedVariant = abTest.abTestVariants.some((variant) => {
        const checkoutId = variant.abTestWidgets?.find((w: any) => w.type === AbTestWidgetType.CHECKOUT)?.externalId;
        const checkout = checkouts.find((c) => c.id === checkoutId);
        return !checkout || !checkout?.name;
      });

      if (anyDeletedVariant) {
        showToast({
          message: "One or more variants have been deleted. You can't set the winner for this test.",
          mode: 'danger',
        });
        return;
      }
    }

    // For cross-site/theme tests, use simple confirmation modal; for checkout tests, use EndAbTestModal
    if (isThemeTest) {
      const variantB = abTest.abTestVariants.find((v) => v.name === 'B') || abTest.abTestVariants[1];
      if (variantB?.id) {
        setSetAsWinnerOpen({ show: true, id: variantB.id });
      }
    } else {
      setEndAbTestOpen(true);
    }
  }

  return (
    <>
      <div className="ab-test-card">
        <div className="ab-test-card__header">
          <div className="ab-test-card__header-info">
            <div className="ab-test-card__title-container">
              <h2 className="ab-test-card__title">{abTest.name}</h2>
              <Tag
                value={AbTestWidgetTypeLabels[abTest.testType]}
                size="large"
                padding={true}
                color="var(--on-surface-default)"
                textColor="var(--surface-bright)"
              />
            </div>
            <div className="ab-test-card__description">{abTest.description}</div>
          </div>
          <div className="ab-test-card__header-action">
            {isConcludedMode && abTest.startedAt && abTest.endedAt && (
              <div className="ab-test-card__date-range">{formatAbTestDateRange(abTest.startedAt, abTest.endedAt)}</div>
            )}
            <StatusLabel status={getAbTestStatusText(abTest)} />
            <ThreeDotMenu
              items={getAbTestThreeDotMenuItems(abTest, modalActions, angularRedirect, showToast, winningSegmentClean)}
            />
          </div>
        </div>
        <div className="ab-test-card__rules-section">
          {!getIsThemeTest(abTest.testType) && (
            <div className="ab-test-card__rules-info">
              {abTest.abTestRules?.ruleGroups?.length > 0 ? (
                <>
                  <h3 className="ab-test-card__subtitle">Test Rules</h3>
                  <RuleChips abTestRules={abTest.abTestRules} />
                </>
              ) : (
                <a>No rules have been set for this test</a>
              )}
            </div>
          )}
          {showSetWinnerButton && (
            <div className="ab-test-card__rules-action">
              <Button
                className="ab-test-card__set-winner-button"
                onClick={() => handleSetWinner(abTest)}
                text={getWinnerButtonText(abTest.testType)}
                size={'medium'}
                variant={'tertiary'}
              />
            </div>
          )}
        </div>

        {getAbTestStatusText(abTest) === AbTestType.CONCLUDED && (
          <div className="ab-test-card__metrics-section">
            <div className="ab-test-card__annualized-lift">
              <h3 className="ab-test-card__metrics-title">Total annualized lift</h3>
              {totalAnnualizedLiftDisplay ? (
                <div className={`ab-test-card__lift-amount${totalAnnualizedLiftDisplay.isNegative ? '-negative' : ''}`}>
                  {totalAnnualizedLiftDisplay.formatted}
                </div>
              ) : (
                <div className="ab-test-card__lift-amount-fallback">
                  <NoDataFallback show={true} />
                </div>
              )}
            </div>
            {!getIsThemeTest(abTest.testType) && (
              <div className="ab-test-card__user-comments">
                <h3 className="ab-test-card__metrics-title">User comments</h3>
                <div className="ab-test-card__comments-text">
                  {abTest.testComments && abTest.testComments.length > 0 ? abTest.testComments : 'No comments added'}
                </div>
              </div>
            )}
          </div>
        )}

        <div
          className={`ab-test-card__tables-container ${abTest.abTestVariants?.length > 2 ? 'ab-test-card__tables-container--single' : ''}`}>
          <TestVariantsTable
            variants={testVariants}
            isDraftMode={isDraftMode}
            isLoading={isTestVariantsLoading}
            testType={abTest.testType}
            abTestId={abTest.id}
            showSetWinnerButton={
              isConcludedMode &&
              abTest.winnerAt === null &&
              abTest.abTestVariants?.length > 2 &&
              (abTest.testType === AbTestTypeCategory.SHIPPING_RATES || getIsWinnerAppliedInShopify(abTest.testType))
            }
            onSetWinner={(variantId) => {
              setSetAsWinnerOpen({ show: true, id: variantId });
            }}
            abTestVariants={abTest.abTestVariants}
          />
          {!isDraftMode && abTest.abTestVariants?.length === 2 && (
            <AbTestSegmentsMetricsTable
              segments={
                abTest.outlierFilter === ABTestOutlierFilterOption.WITHOUT_OUTLIER
                  ? segmentsMetricDataNoOutliers
                  : segmentsMetricData
              }
              isLoading={isSegmentMetricLoading}
              testType={abTest.testType}
            />
          )}
        </div>
      </div>
      <AbTestModals
        abTest={abTest}
        abTestActions={abTestActions}
        modalStates={modalStates}
        modalActions={modalActions}
        runningAbTestCheckoutIds={runningAbTestCheckoutIds}
        setActiveTab={setActiveTab}
        checkouts={checkouts}
        setAsWinnerOpen={setAsWinnerOpen}
        setSetAsWinnerOpen={setSetAsWinnerOpen}
        forceSetAsWinnerOpen={forceSetAsWinnerOpen}
        setForceSetAsWinnerOpen={setForceSetAsWinnerOpen}
        angularRedirect={angularRedirect}
      />

      {endAbTestOpen &&
        (abTest.abTestVariants?.length === 2 ? (
          <EndAbTestModal
            title={`End "${abTest?.name}"`}
            buttonText={'Review'}
            abTest={abTest}
            checkouts={checkouts}
            segments={segmentsMetricData}
            segmentsNoOutliers={segmentsMetricDataNoOutliers}
            outlierFilter={reviewModalData?.outlierFilter ?? null}
            isOpen={endAbTestOpen}
            comments={reviewModalData?.comments}
            selectedActionOption={
              reviewModalData?.selectedActionOption ?? AbTestWinnerOptions.implementTestWinnerInCheckout
            }
            selectedSegments={reviewModalData?.selectedSegments ?? null}
            onClose={() => {
              setEndAbTestOpen(false);
            }}
            onSuccess={async ({ comments, selectedActionOption, selectedSegments, outlierFilter }) => {
              if (selectedActionOption === AbTestWinnerOptions.dontImplementAnyChange) {
                const originalSegments =
                  outlierFilter === ABTestOutlierFilterOption.WITHOUT_OUTLIER
                    ? segmentsMetricDataNoOutliers
                    : segmentsMetricData;
                const winnerVariantDto = createAbTestWinnerVariantDto(
                  abTest.id!,
                  abTest.name,
                  getWinnerVariantId(selectedActionOption),
                  selectedActionOption,
                  comments,
                  selectedSegments,
                  freeShippingThreshold,
                  bigSpenderThreshold,
                  outlierFilter,
                  originalSegments,
                  totalAnnualizedLift,
                  null,
                );
                await abTestActions.setWinnerPredictedSegmentsV2(abTest.id, winnerVariantDto);
                setEndAbTestOpen(false);
              } else {
                setReviewModalData({
                  comments,
                  selectedActionOption,
                  selectedSegments,
                  outlierFilter,
                });
                setEndAbTestOpen(false);
                setReviewAbTestModalOpen(true);
              }
            }}
            onActionOptionChanged={async () => {
              // Optional: Handle action option changes if needed
            }}
          />
        ) : (
          <ConfirmationModal
            title={'End A/B test?'}
            buttonText={'End test'}
            mainContent={`You're ending A/B test "${abTest?.name}"`}
            isOpen={endAbTestOpen}
            onClose={() => {
              setEndAbTestOpen(false);
            }}
            onSuccess={async () => {
              await abTestActions.endTest(abTest.id);
              setEndAbTestOpen(false);
            }}
          />
        ))}

      {reviewAbTestModalOpen && reviewModalData && reviewModalData.selectedSegments && (
        <ReviewAbTestModal
          buttonText="Approve"
          abTest={abTest}
          totalAnnualizedLift={totalAnnualizedLift}
          selectedActionOption={reviewModalData.selectedActionOption}
          // Construct pairs on-demand from stored segments
          segmentPairs={createAbTestSegmentPairs(
            reviewModalData.selectedSegments,
            freeShippingThreshold,
            bigSpenderThreshold,
          )}
          outlierFilter={reviewModalData.outlierFilter}
          isOpen={reviewAbTestModalOpen}
          onClose={() => {
            setReviewAbTestModalOpen(false);
            // Optionally reopen EndAbTestModal with saved data
            setEndAbTestOpen(true);
          }}
          onSuccess={async ({ revenueUnlock }) => {
            if (!reviewModalData || !reviewModalData.selectedSegments) return;

            const winnerVariantId = getWinnerVariantId(reviewModalData.selectedActionOption);
            const originalSegments =
              reviewModalData.outlierFilter === ABTestOutlierFilterOption.WITHOUT_OUTLIER
                ? segmentsMetricDataNoOutliers
                : segmentsMetricData;

            // Fetch totalAnnualizedLift with the user's selected outlier filter at approve time
            const isThemeTest = getIsThemeTest(abTest.testType);
            const segment = isThemeTest ? 'all_users' : 'all_checkouts';
            const testTypeCategory = abTest.testType || AbTestTypeCategory.SHIPPING_RATES;
            const outlierParam = reviewModalData.outlierFilter ? `&outlierFilter=${reviewModalData.outlierFilter}` : '';
            let freshTotalAnnualizedLift: Array<{ segment: string; annualizedUpliftArpc: number }> | null = null;
            try {
              const response = await abTestingApi.get(
                `/ab-tests/total-annualized-lift/${abTest.id}?segments=${segment}&testTypeCategory=${testTypeCategory}${outlierParam}`,
              );
              if (response?.data && Array.isArray(response.data)) {
                freshTotalAnnualizedLift = response.data.map((row: Record<string, any>) => ({
                  segment: row.Segment,
                  annualizedUpliftArpc: row.annualizedUpliftArpc,
                }));
              }
            } catch (error) {
              console.error('Error fetching total annualized lift for winner snapshot:', error);
            }

            const winnerVariantDto = createAbTestWinnerVariantDto(
              abTest.id!,
              abTest.name,
              winnerVariantId,
              reviewModalData.selectedActionOption,
              reviewModalData.comments,
              reviewModalData.selectedSegments,
              freeShippingThreshold,
              bigSpenderThreshold,
              reviewModalData.outlierFilter,
              originalSegments,
              freshTotalAnnualizedLift,
              revenueUnlock,
            );

            await abTestActions.setWinnerPredictedSegmentsV2(abTest.id, winnerVariantDto);
            setTotalAnnualizedLift(freshTotalAnnualizedLift);
            setReviewAbTestModalOpen(false);
          }}
        />
      )}
    </>
  );
}
